import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { Calendar, ArrowLeft } from 'lucide-react';

const BlogPost = () => {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [author, setAuthor] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                // Try fetching by ID first (backward compatibility)
                const docRef = doc(db, 'posts', id);
                const docSnap = await getDoc(docRef);

                let postData = null;

                if (docSnap.exists()) {
                    postData = { id: docSnap.id, ...docSnap.data() };
                } else {
                    // Try fetching by Slug if ID fails
                    const q = query(collection(db, 'posts'), where('slug', '==', id));
                    const querySnapshot = await getDocs(q);

                    if (!querySnapshot.empty) {
                        const postDoc = querySnapshot.docs[0];
                        postData = { id: postDoc.id, ...postDoc.data() };
                    }
                }

                if (postData) {
                    setPost(postData);

                    // Fetch Author if exists
                    if (postData.authorId) {
                        try {
                            const authorRef = doc(db, 'authors', postData.authorId);
                            const authorSnap = await getDoc(authorRef);
                            if (authorSnap.exists()) {
                                setAuthor({ id: authorSnap.id, ...authorSnap.data() });
                            }
                        } catch (err) {
                            console.error("Error fetching author:", err);
                        }
                    }
                } else {
                    setPost(null);
                }
            } catch (error) {
                console.error("Error fetching post: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [id]);

    if (loading) {
        return (
            <div className="pt-32 pb-20 text-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A2CB52] mx-auto"></div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="pt-32 pb-20 text-center min-h-screen px-4">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h1>
                <Link to="/blog" className="text-[#A2CB52] font-bold hover:underline">
                    &larr; Back to Blog
                </Link>
            </div>
        );
    }

    return (
        <article className="pt-24 pb-20 min-h-screen bg-white">
            <SEO
                title={`${post.title} | BPO Consultants Blog`}
                description={post.excerpt}
                image={post.imageUrl}
            />

            {/* Hero Image */}
            {post.imageUrl && (
                <div className="w-full h-64 md:h-96 relative mb-8">
                    <img
                        src={post.imageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30"></div>
                </div>
            )}

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <FadeIn>
                    <Link to="/blog" className="inline-flex items-center text-gray-500 hover:text-[#A2CB52] mb-6 transition-colors font-medium">
                        <ArrowLeft size={18} className="mr-2" /> Back to Blog
                    </Link>

                    <h1 className="text-3xl md:text-5xl font-bold text-[#20222D] mb-6 leading-tight">
                        {post.title}
                    </h1>

                    <div className="flex items-center text-gray-500 mb-8 border-b border-gray-100 pb-8">
                        <Calendar size={18} className="mr-2" />
                        {post.createdAt?.seconds ? new Date(post.createdAt.seconds * 1000).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        }) : 'Date N/A'}
                    </div>

                    <div
                        className="prose prose-lg max-w-none break-words text-left prose-headings:text-[#20222D] prose-a:text-[#A2CB52] prose-img:rounded-xl mb-16"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {/* Author Bio Section */}
                    {author && (
                        <div className="bg-gray-50 rounded-2xl p-8 flex flex-col md:flex-row items-center md:items-start gap-6 border border-gray-100">
                            <div className="shrink-0">
                                {author.avatarUrl ? (
                                    <img
                                        src={author.avatarUrl}
                                        alt={author.name}
                                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-sm"
                                    />
                                ) : (
                                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                                        No Photo
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <h3 className="text-xl font-bold text-[#20222D] mb-1">
                                    About {author.name}
                                </h3>
                                <p className="text-[#A2CB52] font-semibold text-sm mb-3 uppercase tracking-wide">
                                    {author.role}
                                </p>
                                <p className="text-gray-600 mb-4">
                                    {author.bio}
                                </p>
                                <Link
                                    to={`/author/${author.slug || author.id}`}
                                    className="text-[#20222D] font-bold hover:text-[#A2CB52] transition-colors inline-flex items-center"
                                >
                                    View Profile &rarr;
                                </Link>
                            </div>
                        </div>
                    )}
                </FadeIn>
            </div>
        </article>
    );
};

export default BlogPost;
